import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Package, DollarSign, Hash, Download, FileText, FileSpreadsheet, File, Calendar, FileImage, FileType } from 'lucide-react';
import apiService from '../services/apiService';

const ProductCard = ({ product, index, viewMode = 'grid' }) => {
  const { t } = useTranslation();
  const getStockStatus = (stock) => {
    if (stock > 100) return { color: 'text-green-600', bg: 'bg-green-100', label: 'Alto Stock' };
    if (stock > 50) return { color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Stock Medio' };
    return { color: 'text-red-600', bg: 'bg-red-100', label: 'Stock Bajo' };
  };

  const getImageUrl = (image) => {
    if (!image) return 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=400&fit=crop';
    if (image.startsWith('http') || image.startsWith('data:')) return image;
    return `${apiService.baseURL}${image}`;
  };

  const getDocumentUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http')) return url;
    return `${apiService.baseURL}${url}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const getFileIcon = (filename) => {
    if (!filename) return <File className="w-4 h-4" />;
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf': return <FileText className="w-4 h-4" />;
      case 'doc':
      case 'docx': return <FileType className="w-4 h-4" />;
      case 'xls':
      case 'xlsx':
      case 'csv': return <FileSpreadsheet className="w-4 h-4" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'webp': return <FileImage className="w-4 h-4" />;
      default: return <File className="w-4 h-4" />;
    }
  };

  const getFileDisplayName = (filename) => {
    if (!filename) return 'Archivo';
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.')) || filename;
    const extension = filename.split('.').pop()?.toUpperCase() || '';
    // Tomar las primeras 8 caracteres del nombre
    const shortName = nameWithoutExt.length > 8 ? nameWithoutExt.substring(0, 8) + '...' : nameWithoutExt;
    return `${shortName}.${extension}`;
  };

  const stockStatus = getStockStatus(product.stock); // Mantener esto si el stock se usa internamente

  // Vista detallada (horizontal)
  if (viewMode === 'detailed') {
    return (
      <motion.div
        className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: index * 0.05 }}
        whileHover={{ x: 5 }}
      >
        <div className="flex flex-col md:flex-row">
          <div className="relative w-full md:w-72 h-48 md:h-auto overflow-hidden flex-shrink-0">
            <img 
              src={getImageUrl(product.image_url)} 
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
          
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {product.description}
                </p>
              </div>
              <div className="ml-4 text-right flex-shrink-0">
                <div className="bg-gradient-to-r from-green-400 to-emerald-500 px-4 py-3 rounded-xl shadow-md">
                  <p className="text-base font-bold text-white leading-tight">
                    {product.short_description || 'Producto B2B'}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-6 mb-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-gray-700">
                  {formatDate(product.updated_at || product.updatedAt)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500 font-mono">
                  {product.sku || 'N/A'}
                </span>
              </div>
            </div>
            
            {product.documents && product.documents.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {product.documents.map((file, fileIndex) => (
                  <motion.a
                    key={fileIndex}
                    href={getDocumentUrl(file.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 text-sm"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {getFileIcon(file.name)}
                    <span className="max-w-[150px] truncate">{getFileDisplayName(file.name)}</span>
                    <Download className="w-4 h-4" />
                  </motion.a>
                ))}
              </div>
            ) : (
              <button className="bg-gray-200 text-gray-600 px-4 py-2 rounded-lg font-semibold cursor-not-allowed text-sm" disabled>
                {t('messages.noResults')}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  // Vista normal (grid o list)
  return (
    <motion.div
      className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -3 }}
    >
      <div className="relative h-48 overflow-hidden">
        <img 
          src={getImageUrl(product.image_url)} 
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        {/* Se elimina el indicador de stock visual en la tarjeta */}
      </div>
      
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-900 line-clamp-2 flex-1">
            {product.name}
          </h3>
        </div>
        
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {product.description}
        </p>
        
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-green-400 to-emerald-500 px-4 py-3 rounded-xl shadow-md">
            <p className="text-base font-bold text-white text-center leading-tight">
              {product.short_description || 'Producto B2B'}
            </p>
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-gray-700">
                Actualizado: {formatDate(product.updated_at || product.updatedAt)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Hash className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500 font-mono">
                {product.sku || 'N/A'}
              </span>
            </div>
          </div>
        </div>
        
        {product.documents && product.documents.length > 0 ? (
          <div className="mt-4 space-y-2">
            {product.documents.map((file, fileIndex) => (
              <motion.a
                key={fileIndex}
                href={getDocumentUrl(file.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-200 text-sm"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {getFileIcon(file.name)}
                <span className="truncate max-w-[200px]">{getFileDisplayName(file.name)}</span>
                <Download className="w-4 h-4" />
              </motion.a>
            ))}
          </div>
        ) : (
          <motion.button
            className="w-full mt-4 bg-gray-200 text-gray-600 py-3 rounded-xl font-semibold cursor-not-allowed"
            disabled
          >
            {t('messages.noResults')}
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

export default ProductCard;